import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X, Send, Trash2, Brain, Mic, MicOff, GripVertical } from "lucide-react";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMatchCopilot } from "@/hooks/useMatchCopilot";
import { CopilotMessage } from "./CopilotMessage";
import { QuickChips } from "./QuickChips";
import { CopilotDisclaimer } from "./CopilotDisclaimer";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

type GameStatus = 'scheduled' | 'live' | 'in_progress' | 'finished' | 'completed' | string;

interface MatchCopilotDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  fixtureId: number | null;
  gameStatus: GameStatus;
  homeTeam: string;
  awayTeam: string;
  onRemainingMessagesChange?: (count: number) => void;
}

const DEFAULT_PANEL_WIDTH = 672;
const MIN_PANEL_WIDTH = 320;
const MAX_PANEL_WIDTH_PERCENT = 0.8;
const STORAGE_KEY = 'sg7-copilot-panel-width';

export function MatchCopilotDrawer({
  isOpen,
  onClose,
  fixtureId,
  gameStatus,
  homeTeam,
  awayTeam,
  onRemainingMessagesChange,
}: MatchCopilotDrawerProps) {
  const [inputValue, setInputValue] = useState("");
  const { t } = useTranslation('errors');
  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
  const [isMobile, setIsMobile] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const isResizing = useRef(false);
  const navigate = useNavigate();
  
  const {
    messages,
    isLoading,
    error,
    sendMessage,
    clearHistory,
    remainingMessages,
    gameRemainingMessages,
    requiresAuth,
    clearAuthRequired,
    isBalanceLoaded,
  } = useMatchCopilot(fixtureId);

  // Speech recognition hook
  const {
    isListening,
    isSupported: isSpeechSupported,
    transcript,
    toggleListening,
  } = useSpeechRecognition({
    lang: 'pt-PT',
    onResult: (text) => {
      setInputValue(prev => prev ? `${prev} ${text}` : text);
    },
    onError: (error) => {
      if (error === 'not-allowed') {
        toast.error(t('errors:microphoneDenied'));
      } else {
        toast.error(t('errors:speechRecognitionError'));
      }
    },
  });

  // Check if mobile and load saved width
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Load saved width from localStorage
    const savedWidth = localStorage.getItem(STORAGE_KEY);
    if (savedWidth) {
      const parsed = parseInt(savedWidth);
      if (!isNaN(parsed) && parsed >= MIN_PANEL_WIDTH) {
        setPanelWidth(parsed);
      }
    }
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Save width to localStorage when it changes
  useEffect(() => {
    if (!isMobile && panelWidth !== DEFAULT_PANEL_WIDTH) {
      localStorage.setItem(STORAGE_KEY, panelWidth.toString());
    }
  }, [panelWidth, isMobile]);

  // Resize handlers
  const handleResizeMove = useCallback((e: PointerEvent) => {
    if (!isResizing.current) return;
    const newWidth = window.innerWidth - e.clientX;
    const maxWidth = window.innerWidth * MAX_PANEL_WIDTH_PERCENT;
    const clampedWidth = Math.min(Math.max(newWidth, MIN_PANEL_WIDTH), maxWidth);
    setPanelWidth(clampedWidth);
  }, []);

  const handleResizeEnd = useCallback(() => {
    isResizing.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    document.removeEventListener('pointermove', handleResizeMove);
    document.removeEventListener('pointerup', handleResizeEnd);
  }, [handleResizeMove]);

  const handleResizeStart = useCallback((e: React.PointerEvent) => {
    if (isMobile) return;
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('pointermove', handleResizeMove);
    document.addEventListener('pointerup', handleResizeEnd);
  }, [isMobile, handleResizeMove, handleResizeEnd]);

  // Redirect to auth when user tries to send message without being logged in
  useEffect(() => {
    if (requiresAuth) {
      toast.info('Faça login para usar o Match Copilot');
      clearAuthRequired();
      onClose();
      navigate('/auth');
    }
  }, [requiresAuth, clearAuthRequired, onClose, navigate]);

  // Notify parent when remaining messages change
  useEffect(() => {
    onRemainingMessagesChange?.(gameRemainingMessages);
  }, [gameRemainingMessages, onRemainingMessagesChange]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages]);

  // Focus input when drawer opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Cleanup resize listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('pointermove', handleResizeMove);
      document.removeEventListener('pointerup', handleResizeEnd);
    };
  }, [handleResizeMove, handleResizeEnd]);

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const text = inputValue;
    setInputValue("");
    await sendMessage(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChipClick = (text: string) => {
    if (isLoading) return;
    sendMessage(text);
  };

  const panelStyle = isMobile 
    ? { width: '100%', maxWidth: '100%' }
    : { width: `${panelWidth}px`, maxWidth: `${panelWidth}px` };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        side="right" 
        className="p-0 flex flex-col bg-[hsl(var(--sg7-surface-1))] overflow-visible"
        style={panelStyle}
      >
        {/* Resize handle - only on tablet/desktop */}
        {!isMobile && (
          <div
            onPointerDown={handleResizeStart}
            className={cn(
              "absolute left-0 top-0 bottom-0 w-2 -ml-1 cursor-ew-resize",
              "flex items-center justify-center group z-50",
              "hover:bg-[hsl(var(--sg7-gold))]/10 transition-colors"
            )}
          >
            <div className={cn(
              "w-1 h-16 rounded-full transition-all duration-200",
              "bg-[hsl(var(--sg7-stroke-subtle))]",
              "group-hover:bg-[hsl(var(--sg7-gold))] group-hover:h-24",
              "group-active:bg-[hsl(var(--sg7-gold))] group-active:h-32"
            )} />
          </div>
        )}

        {/* Header */}
        <SheetHeader className="px-4 py-3 border-b border-[hsl(var(--sg7-stroke-subtle))] shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[hsl(var(--sg7-gold))] to-[hsl(var(--sg7-gold-dark))] flex items-center justify-center">
                <Brain className="h-4 w-4 text-[hsl(var(--sg7-surface-1))]" />
              </div>
              <div>
                <SheetTitle className="text-base font-semibold text-[hsl(var(--sg7-text-primary))]">
                  Match Copilot SG7
                </SheetTitle>
                <p className="text-xs text-[hsl(var(--sg7-text-muted))]">
                  {homeTeam} vs {awayTeam}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={clearHistory}
                  className="h-8 w-8 text-[hsl(var(--sg7-text-muted))] hover:text-destructive"
                  title="Clear history"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="h-8 w-8 text-[hsl(var(--sg7-text-muted))]"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </SheetHeader>

        {/* Messages */}
        <ScrollArea ref={scrollAreaRef} className="flex-1 px-4 py-3">
          <div className="space-y-3">
            {messages.length === 0 ? (
              <div className="text-center py-8">
                <Brain className="h-12 w-12 mx-auto mb-3 text-[hsl(var(--sg7-gold))] opacity-50" />
                <p className="text-sm text-[hsl(var(--sg7-text-muted))]">
                  Ask me anything about this match!
                </p>
                <p className="text-xs text-[hsl(var(--sg7-text-muted))] mt-1">
                  Picks, stats, predictions, tactics...
                </p>
              </div>
            ) : (
              messages.map((msg, idx) => (
                <CopilotMessage key={idx} role={msg.role} content={msg.content} />
              ))
            )}
            
            {/* Error display */}
            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Quick Chips + Input */}
        <div className="px-4 py-3 border-t border-[hsl(var(--sg7-stroke-subtle))] space-y-3 shrink-0 bg-[hsl(var(--sg7-surface-1))]">
          {/* Quick chips - only show when no messages or few messages */}
          {messages.length < 4 && (
            <QuickChips 
              gameStatus={gameStatus} 
              onChipClick={handleChipClick}
              disabled={isLoading}
            />
          )}
          
          {/* Real-time transcript feedback */}
          {isListening && transcript && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse [animation-delay:0.2s]" />
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse [animation-delay:0.4s]" />
              </div>
              <p className="text-sm text-[hsl(var(--sg7-text))] italic flex-1">
                "{transcript}"
              </p>
            </div>
          )}
          
          {/* Input area */}
          <div className="flex items-center gap-2">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about the match..."
              disabled={isLoading || gameRemainingMessages <= 0}
              className={cn(
                "flex-1 bg-[hsl(var(--sg7-surface-2))] border-[hsl(var(--sg7-stroke-subtle))]",
                "text-[hsl(var(--sg7-text))]",
                "placeholder:text-[hsl(var(--sg7-text-muted))]",
                "focus-visible:ring-[hsl(var(--sg7-gold))]"
              )}
            />
            
            {/* Voice input button */}
            {isSpeechSupported && (
              <Button
                onClick={toggleListening}
                disabled={isLoading || gameRemainingMessages <= 0}
                size="icon"
                variant="ghost"
                className={cn(
                  "shrink-0 transition-colors",
                  isListening 
                    ? "text-red-500 bg-red-500/10 animate-pulse" 
                    : "text-[hsl(var(--sg7-text-muted))] hover:text-[hsl(var(--sg7-gold))] hover:bg-[hsl(var(--sg7-surface-2))]"
                )}
                title={isListening ? "Parar gravação" : "Falar"}
              >
                {isListening ? (
                  <MicOff className="h-4 w-4" />
                ) : (
                  <Mic className="h-4 w-4" />
                )}
              </Button>
            )}
            
            <Button
              onClick={handleSend}
              disabled={!inputValue.trim() || isLoading || gameRemainingMessages <= 0}
              size="icon"
              className={cn(
                "shrink-0",
                "bg-gradient-to-br from-[hsl(var(--sg7-gold))] to-[hsl(var(--sg7-gold-dark))]",
                "hover:from-[hsl(var(--sg7-gold-dark))] hover:to-[hsl(var(--sg7-gold))]",
                "text-[hsl(var(--sg7-surface-1))]",
                "disabled:opacity-50"
              )}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Disclaimer */}
          <CopilotDisclaimer 
            remainingMessages={remainingMessages} 
            gameRemainingMessages={gameRemainingMessages}
            isBalanceLoaded={isBalanceLoaded}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
